'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Store,
  Globe,
  CreditCard,
  Sliders,
  Bell,
  Users,
  History,
  ShieldCheck,
  FileSpreadsheet,
  LogOut
} from 'lucide-react';

interface SettingsSidebarProps {
  currentPath?: string;
}

export function SettingsSidebar({ currentPath }: SettingsSidebarProps) {
  const pathname = usePathname();
  const activePath = currentPath || pathname;

  const businessLinks = [
    { label: 'Negocio', href: '/dashboard/settings', icon: Store },
    { label: 'Tu página de reservas', href: '/dashboard/settings/booking-page', icon: Globe },
    { label: 'Cobros y medios de pago', href: '/dashboard/settings/payment-methods', icon: CreditCard },
    { label: 'Reglas de reserva', href: '/dashboard/settings/booking-rules', icon: Sliders },
    { label: 'Avisos y recordatorios', href: '/dashboard/settings/reminders', icon: Bell },
  ];

  const accessLinks = [
    { label: 'Usuarios y permisos', href: '/dashboard/settings/team', icon: Users },
    { label: 'Historial de cambios', href: '/dashboard/settings/history', icon: History },
    { label: 'Cuenta y plan', href: '/dashboard/settings/subscription', icon: ShieldCheck },
    { label: 'Importar histórico', href: '/dashboard/settings/import', icon: FileSpreadsheet },
  ];

  const handleLogout = async () => {
    if (confirm('¿Deseas cerrar sesión?')) {
      await fetch('/api/auth/logout', { method: 'POST' });
      window.location.href = '/login';
    }
  };

  return (
    <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm space-y-5">
      {/* TU NEGOCIO */}
      <div>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 block mb-2">
          Tu Negocio
        </span>
        <div className="space-y-1">
          {businessLinks.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.href;
            return (
              <Link
                key={item.label}
                href={item.href}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-800 font-bold border border-emerald-200/80 shadow-2xs'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-700' : 'text-slate-400'}`} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ACCESOS */}
      <div className="pt-3 border-t border-slate-100">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 block mb-2">
          Accesos
        </span>
        <div className="space-y-1">
          {accessLinks.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.href;
            return (
              <Link
                key={item.label}
                href={item.href}
                className={`flex items-center gap-2.5 px-3.5 py-2 rounded-2xl text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-800 font-bold border border-emerald-200/80'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-700' : 'text-slate-400'}`} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Cerrar Sesión */}
      <div className="pt-2 border-t border-slate-100">
        <button
          onClick={handleLogout}
          type="button"
          className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-2xl transition-colors cursor-pointer"
        >
          <LogOut className="w-4 h-4" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </div>
  );
}
